import { type TFunction } from "i18next"

import type {
  DocumentLabelInfo,
  FieldComparison,
  RecommendedSourceSummary,
  SourceType
} from "./types.ts"

interface DocumentLabels {
  proposal?: DocumentLabelInfo
  contract?: DocumentLabelInfo
}

/** One source, and the changed fields the backend recommends taking from
 * it. More than one entry means the recommendation is genuinely mixed —
 * the modal must say so rather than naming a single document. */
interface RecommendedSourceEntry {
  source: SourceType
  fieldLabels: string[]
}

const SOURCE_LABEL_KEY: Record<SourceType, string> = {
  lead: "leadApproval.confirmProjectInfo.sourceLead",
  proposal: "leadApproval.confirmProjectInfo.sourceProposal",
  contract: "leadApproval.confirmProjectInfo.sourceContract"
}

/**
 * The document's version, but only when there is a real one to show.
 * `version_display` carries `"v3"` when the backend decided this
 * document's lineage has actually been versioned, and the status display
 * ("Draft", "Approved") when it hasn't — a bare "v1" says nothing about a
 * document that was never versioned, so only the former is a version here.
 */
function versionLabel(info: DocumentLabelInfo | undefined): string | undefined {
  const display = info?.version_display
  if (!display) return undefined
  return /^v\d+$/i.test(display) ? display.toUpperCase() : undefined
}

function documentInfo(
  source: SourceType,
  labels: DocumentLabels | undefined
): DocumentLabelInfo | undefined {
  if (source === "proposal") return labels?.proposal
  if (source === "contract") return labels?.contract
  return undefined
}

/**
 * "Contract · V3 · Approved" — the document, which version of it, and what
 * state it is in, degrading cleanly to just "Contract" when the response
 * carries no label info. The Lead has no version or status concept and is
 * always just "Lead".
 */
function formatSourceLabel(
  source: SourceType,
  labels: DocumentLabels | undefined,
  t: TFunction
): string {
  const name = t(SOURCE_LABEL_KEY[source])
  if (source === "lead") return name

  const info = documentInfo(source, labels)
  const version = versionLabel(info)
  const status = info?.status_display

  if (version && status) {
    return t("leadApproval.confirmProjectInfo.sourceWithVersionAndStatus", {
      source: name,
      version,
      status
    })
  }
  if (version) {
    return t("leadApproval.confirmProjectInfo.sourceWithVersion", {
      source: name,
      version
    })
  }
  if (status) {
    return t("leadApproval.confirmProjectInfo.sourceWithStatus", {
      source: name,
      status
    })
  }
  return name
}

/** The column header's second line — version and status without repeating
 * the document's own name, which the header already shows. */
function formatColumnSublabel(
  info: DocumentLabelInfo | undefined
): string | undefined {
  if (!info) return undefined
  const parts = [versionLabel(info), info.status_display].filter(Boolean)
  return parts.length ? parts.join(" · ") : undefined
}

/**
 * Which source each changed field is recommended from, grouped — the
 * backend picks `recommended_source` per field, so a run where the
 * Contract supplies the dates and the Lead keeps its own description is
 * normal, and claiming "all values come from the Contract" would be a
 * lie. Ordered by how many fields each source supplies, then by the order
 * they first appear, so the dominant source reads first.
 */
function buildRecommendedSourceEntries(
  diffs: FieldComparison[]
): RecommendedSourceEntry[] {
  const order: SourceType[] = []
  const bySource = new Map<SourceType, string[]>()

  diffs
    .filter((diff) => diff.has_diff)
    .forEach((diff) => {
      if (!bySource.has(diff.recommended_source)) {
        bySource.set(diff.recommended_source, [])
        order.push(diff.recommended_source)
      }
      bySource.get(diff.recommended_source)!.push(diff.label)
    })

  return order
    .map((source) => ({ source, fieldLabels: bySource.get(source)! }))
    .sort((a, b) => b.fieldLabels.length - a.fieldLabels.length)
}

/**
 * The single-source headline, when there is one. Prefers the per-field
 * breakdown (the actual values "use recommended" would apply) and falls
 * back to the backend's own overall summary when nothing differs, so the
 * radio still names a source on a no-difference run.
 */
function formatSingleRecommendedSource(
  entries: RecommendedSourceEntry[],
  summary: RecommendedSourceSummary | undefined,
  labels: DocumentLabels | undefined,
  t: TFunction
): string | undefined {
  const source =
    entries.length === 1
      ? entries[0].source
      : entries.length === 0
        ? summary?.source
        : undefined
  if (!source) return undefined

  return t("leadApproval.confirmProjectInfo.recommendedSingleSource", {
    source: formatSourceLabel(source, labels, t)
  })
}

export {
  buildRecommendedSourceEntries,
  formatColumnSublabel,
  formatSingleRecommendedSource,
  formatSourceLabel,
  type DocumentLabels,
  type RecommendedSourceEntry
}
